
import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { FaChartLine } from 'react-icons/fa';
import yardsData from "../assets/yards.json";
import YardDropdown from "./YardDropdown";

export default function SpeedTestDashboard() {
  const [deviceData, setDeviceData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [yardFilter, setYardFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const serverUrl = process.env.REACT_APP_API_SERVER_URL || 'http://10.153.28.40:8080';
      const serverIp = serverUrl.replace('http://', '').replace('https://', '').split(':')[0];
      let uniqueLatestDevices = [];

      try {
        const deviceRes = await axios.get(`${serverUrl}/maiApi/speedtest-overview`);
        let rawData = deviceRes.data;
        if (!Array.isArray(rawData)) {
          console.warn(
            '[MAI Speed test] speedtest-overview: expected an array, got',
            typeof rawData
          );
          rawData = [];
        }
        console.log(
          '[MAI Speed test] Backend OK — GET /maiApi/speedtest-overview succeeded (API reached; ClickHouse query completed on server).'
        );
        if (rawData.length === 0) {
          console.log(
            '[MAI Speed test] No data: empty list — nothing to show in the table. No matching telemetry in the last 3 months for this query, or no rows.'
          );
        } else {
          console.log(
            `[MAI Speed test] Received ${rawData.length} raw row(s) before dedupe by device.`
          );
        }

        // Deduplicate and get latest record per android_id ---
        const latestDeviceDataMap = new Map();
        rawData.forEach(item => {
          if (!latestDeviceDataMap.has(item.android_id) ||
              new Date(item.date_time) > new Date(latestDeviceDataMap.get(item.android_id).date_time)) {
            latestDeviceDataMap.set(item.android_id, item);
          }
        });
        uniqueLatestDevices = Array.from(latestDeviceDataMap.values());
      

        setDeviceData(uniqueLatestDevices); 
      } catch (err) {
        console.warn('[MAI Speed test] GET /maiApi/speedtest-overview failed:', err?.message || err);
        setError("Could not load data from the server. Please check the connection.");
      } finally {
        setLoading(false);
      }

      try {
        const alertRes = await axios.get(`${serverUrl}/maiApi/alerts`);
        setAlerts(Array.isArray(alertRes.data) ? alertRes.data : []);
      } catch (alertErr) {
        console.error("Failed to fetch alerts:", alertErr);
        setAlerts([]);
      } finally {
        setLoadingAlerts(false);
      }
    };

    fetchData(); // Fetch immediately
    const interval = setInterval(fetchData, 600000); // 10 minutes
    return () => clearInterval(interval);
  }, []); // Empty dependency array means this runs once on mount

  // Apply filters to the de-duplicated device list
  const filteredData = deviceData
    .filter(item => (item.android_id || '').toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(item => yardFilter ? item.yard_name === yardFilter : true);

  // Alert summaries (deduplicated)
  const slowPerformanceAlerts = alerts.filter(a => a.alert_type === 'SLOW_PERFORMANCE');
  const deviceOutageAlerts = alerts.filter(a => a.alert_type === 'NETWORK_OUTAGE');
  const uniqueAllAlertsCount = (() => {
    const s = new Set();
    (alerts || []).forEach(a => { if (a?.android_id) s.add(String(a.android_id)); });
    return s.size;
  })();
  const uniqueSlowDeviceCount = (() => {
    const s = new Set();
    slowPerformanceAlerts.forEach(a => {
      if (a.android_id) s.add(String(a.android_id));
    });
    return s.size;
  })();
  const uniqueOutageDeviceCount = (() => {
    const s = new Set();
    deviceOutageAlerts.forEach(a => {
      if (a.android_id) s.add(String(a.android_id));
    });
    return s.size;
  })();

  return (
    <div className="p-8">
      <h1 className="text-3xl font-semibold mb-6 text-center">Speed Test</h1>
      <p className="text-sm text-gray-500 mb-6 text-center">
        Kill switch (enable/disable manual and auto speed tests) is in the navbar under <strong>Speed Test</strong>.
      </p>

      {/* Alerts Section */}
      <div className="alerts-container mb-10 p-4 border border-gray-300 rounded-lg shadow-md bg-gray-50">
        <div className="flex justify-between items-center mb-4"> {/* Added flex container */}
          <h2 className="text-2xl font-bold">Alerts</h2>
          {/* View All Alerts Button */}
          <button
            onClick={() => navigate('/alerts')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            title={`Unique devices with alerts (all): ${uniqueAllAlertsCount}`}
          >
            View All Alerts ({uniqueAllAlertsCount})
          </button>
        </div>

        {loadingAlerts ? (
  <p>Loading alerts...</p>
) : error ? (
  <p style={{ color: "red" }}>{error}</p>
) : (
  <div className="text-gray-700 space-y-2">
    <p>Yards with Slow Performance: <strong>{uniqueSlowDeviceCount}</strong></p>
    <p>Network Outages: <strong>{uniqueOutageDeviceCount}</strong></p>
  </div>
)}

      </div>

      {/* Real-Time Device List Section */}
      <h2 className="text-2xl font-bold mb-4">Speed Test Information</h2>

      {/* Filter controls */}
      <div className="mb-4 flex flex-wrap gap-4 items-center">
        <input
          type="text"
          placeholder="Search by Android ID"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full sm:w-1/3 p-2 border border-gray-300 rounded"
        />
        
        <YardDropdown value={yardFilter} onChange={setYardFilter} />

      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="w-full border border-gray-300 text-center table-auto">
          <thead className="bg-blue-700 text-white">
            <tr>
              <th className="p-3 border">Android ID</th>
              <th className="p-3 border">Last Seen</th>
              <th className="p-3 border">Yard</th>
              <th className="p-3 border">Download (Mbps)</th>
              <th className="p-3 border">Upload</th>
              <th className="p-3 border">Ping (ms)</th>
              <th className="p-3 border">LTE (dBm)</th>
              <th className="p-3 border">Carrier</th>
              <th className="p-3 border">History</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="p-4 text-center">Loading...</td></tr>
            ) : filteredData.length > 0 ? (
              filteredData.map((item) => (
                <tr key={item.android_id} className={`border-t hover:bg-blue-50 ${item.is_outage ? 'bg-red-100' : ''}`}>
                  <td className="p-3 border">{item.android_id}</td>
                  <td className="p-3 border">{new Date(item.last_updated_time).toLocaleString()}</td>
                  <td className="p-3 border">
                    {(() => {
                      // C7 FIX: yard_name can be null for unassigned devices — guard
                      // with optional chaining to prevent TypeError crash
                      const yardName = item.yard_name || '';
                      const yard = yardsData.find(
                        (y) => yardName && `${y.City.toUpperCase()}, ${y.State.toUpperCase()}` === yardName.toUpperCase()
                      );
                      return yard ? `${item.yard_name} ${yard.Yard}` : item.yard_name || "Unknown";
                    })()}
                  </td>
                  <td className={`p-3 border font-bold ${parseFloat(item.download_mbps) < 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {item.download_mbps || '-'}
                  </td>
                  <td className={`p-3 border font-bold ${parseFloat(item.upload_mbps) < 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {item.upload_mbps || "-"}
                  </td>
                  <td className="p-3 border">{item.ping_ms || '-'}</td>
                  <td className="p-3 border">{item.lteDbm || "-"}</td>
                  <td className="p-3 border">{item.carrier || item.network_operator || '-'}</td>
                  <td className="p-3 border align-middle">
                    <button
                      onClick={() => navigate(`/device/${item.android_id}/speed-test`)}
                      title="View historical speed test chart"
                      className="flex justify-center items-center w-full text-blue-600 hover:text-blue-800"
                    >
                      <FaChartLine size="1.5em" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="9" className="p-4 text-center">No devices found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}